import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { has, isObject } from 'lodash-es';
import {
  ToasterService,
  ToastStatus,
} from 'src/app/core/toaster/toaster.service';
import { User } from 'src/app/core/user';
import { validateUUID } from 'src/app/core/utils';
import { SessionService } from '../session.service';
import { CompanyInfo } from 'src/app/core/company-info';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  isLoading = false;
  serviceUnavailable = false;
  private retryInterval: ReturnType<typeof setInterval> | null = null;

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  companyInfo: CompanyInfo = {
    fullName: '',
    shortName: '',
    mobile: '',
    email: '',
  };

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private toasterService: ToasterService
  ) {}

  onSubmit() {
    if (this.loginForm.valid) {
      this.isLoading = true;
      let loginData = this.loginForm.value;
      this.sessionService
        .login(loginData.username || null, loginData.password || null)
        .subscribe({
          next: user => {
            this.isLoading = false;
            this.handleUser(user);
          },
          error: error => {
            this.isLoading = false;
            if (
              error.status === 0 ||
              error.status === 502 ||
              error.status === 503 ||
              error.status === 504
            ) {
              // Service unavailable
              this.serviceUnavailable = true;
              this.startHealthCheck();
            } else if (error.status == 403) {
              // Forbidden
              this.toasterService.toast(
                ToastStatus.warning,
                'This account has been disabled due to payments in arrears..',
                'Login failed'
              );
            } else {
              this.toasterService.toast(
                ToastStatus.warning,
                'Please check your username and password.',
                'Login failed'
              );
            }
          },
        });
    }
  }

  handleUser(user: Partial<User>) {
    if (isObject(user) && has(user, 'id') && validateUUID(user.id ?? '')) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    // Check if redirected due to service unavailable
    if (this.sessionService.isServiceUnavailable()) {
      this.serviceUnavailable = true;
      this.sessionService.setServiceUnavailable(false);
      this.startHealthCheck();
    }

    this.sessionService.fetchCompanyInfo().subscribe({
      next: info => {
        console.log('LOGIN PAGE COMPANY INFO: ', info);
        this.companyInfo = info;
        // If we got company info, service is available
        if (this.serviceUnavailable) {
          this.serviceUnavailable = false;
          this.stopHealthCheck();
        }
      },
      error: error => {
        console.error('LOGIN PAGE COMPANY INFO ERROR', error);
        if (
          error.status === 0 ||
          error.status === 502 ||
          error.status === 503 ||
          error.status === 504
        ) {
          this.serviceUnavailable = true;
          this.startHealthCheck();
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.stopHealthCheck();
  }

  private startHealthCheck(): void {
    if (this.retryInterval) return;
    // Check every 10 seconds if service is back
    this.retryInterval = setInterval(() => {
      this.sessionService.fetchCompanyInfo().subscribe({
        next: () => {
          this.serviceUnavailable = false;
          this.stopHealthCheck();
        },
        error: () => {
          // Still unavailable, keep checking
        },
      });
    }, 10000);
  }

  private stopHealthCheck(): void {
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
  }

  dismissServiceError(): void {
    this.serviceUnavailable = false;
    this.stopHealthCheck();
  }
}
